import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Check, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { ADD_PHOTOS_TO_ALBUM, CREATE_PHOTO_ALBUM, PHOTO_ALBUMS } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import type { PhotoAlbum } from '../types'

interface Props {
  projectId: string
  photoIds: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone?: () => void
}

const AddToAlbumDialog: React.FC<Props> = ({ projectId, photoIds, open, onOpenChange, onDone }) => {
  const { t } = useTranslation()
  const [albumId, setAlbumId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { data } = useQuery<{ photoAlbums: PhotoAlbum[] }>(PHOTO_ALBUMS, { variables: { projectId }, skip: !projectId || !open })
  const [create, { loading: creating }] = useMutation(CREATE_PHOTO_ALBUM, { context: { handlesErrors: true } })
  const [addPhotos, { loading: adding }] = useMutation(ADD_PHOTOS_TO_ALBUM, {
    context: { handlesErrors: true },
    // Object form: Apollo 2 refetches a name only while a mounted query watches it, and this
    // dialog's own list query is skipped once it closes — the album list would stay stale.
    refetchQueries: [{ query: PHOTO_ALBUMS, variables: { projectId } }, 'photos'],
  })
  const busy = creating || adding

  useEffect(() => {
    if (open) {
      setAlbumId(null)
      setNewName('')
      setError(null)
    }
  }, [open])

  const trimmed = newName.trim()
  const creatingNew = trimmed.length > 0
  const canSubmit = !busy && photoIds.length > 0 && (creatingNew ? trimmed.length <= 100 : !!albumId)

  const submit = async () => {
    if (!canSubmit) return
    try {
      let target = albumId
      if (creatingNew) {
        const res: any = await create({ variables: { projectId, name: trimmed } })
        target = res.data.createPhotoAlbum.id
      }
      await addPhotos({ variables: { albumId: target, photoIds } })
      toastr.success('', t('Photos_album_added', { count: photoIds.length }))
      onOpenChange(false)
      onDone?.()
    } catch (e: any) {
      setError(describePhotosError(photosErrorKey(e), t))
    }
  }

  const albums = data?.photoAlbums ?? []

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Photos_album_add_title', { count: photoIds.length })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {albums.length > 0 && (
            <div className="max-h-60 space-y-1 overflow-y-auto" role="listbox" aria-label={t('Photos_album_pick')}>
              {albums.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  role="option"
                  aria-selected={!creatingNew && albumId === a.id}
                  disabled={busy}
                  onClick={() => { setAlbumId(a.id); setNewName('') }}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-accent',
                    !creatingNew && albumId === a.id && 'border-primary bg-accent',
                  )}
                >
                  <span className="truncate">{a.name}</span>
                  {!creatingNew && albumId === a.id && <Check className="h-4 w-4 shrink-0" />}
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="photo-album-new-name">{t('Photos_album_or_new')}</Label>
            <Input
              id="photo-album-new-name"
              maxLength={100}
              value={newName}
              disabled={busy}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('Common_cancel')}</Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {busy && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Common_add')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AddToAlbumDialog
